import React, { useEffect, useRef } from "react";
import { useField, useFormikContext } from "formik";
import {
  formatIncompletePhoneNumber,
  isValidPhoneNumber
} from "libphonenumber-js";
import { ChonkyInput } from "../../ChonkyInput";
import { useIntl } from "react-intl";

/**
 *
 * @param {Object} props
 * @param {import('libphonenumber-js').CountryCode} [props.country = 'US']
 * @param {string} props.name
 * @param {string} [props.required = false]
 * @returns
 */
export const PhoneNumberInput = ({ country = "US", name, ...rest }) => {
  const [field] = useField(name);
  const formik = useFormikContext();
  const intl = useIntl();

  const PHONE_REQUIRED_ERROR = intl.formatMessage({
    id: "contact.error.requiredPhone",
    defaultMessage: "Phone number is required",
    description:
      "Error message that shows when no phone number is provided when it is required."
  });

  const PHONE_INVALID_ERROR = intl.formatMessage({
    id: "contact.error.invalidPhone",
    defaultMessage: "Phone number is invalid",
    description:
      "Error message that shows when phone number is an invalid format."
  });

  const validate = () => {
    if (!field.value && rest.required)
      return formik.setFieldError(name, PHONE_REQUIRED_ERROR);

    // we don't care if it's empty and not required
    if (!field.value) return;

    if (!isValidPhoneNumber(field.value, country)) {
      return formik.setFieldError(name, PHONE_INVALID_ERROR);
    }

    formik.setFieldError(name, "");
  };

  const onBlur = () => validate();

  const handleInput = ({ currentTarget }) => {
    formik.setFieldError(name, "");

    const { value } = currentTarget;
    let formatted = formatIncompletePhoneNumber(value, country);
    /**
     * this is to handle an issue with the libphonenumber-js library as seen in the comments in this:
     * https://gitlab.com/catamphetamine/react-phone-number-input/-/blob/master/source/InputBasic.js on commit SHA 0005d22c7a121eae82cbd7fee3f8c1e2bf202d96 line 38
     *
     * this is a lot simpler then their implementation but it gets the job done
     */
    if (field.value === formatted) {
      formatted = value;
    }

    formik.setFieldValue(name, formatted);
  };

  /**
   * Ignores all keys that are not numbers, or keys for deleting, pasting, moving the cursor or tabbing
   *
   * @param {React.KeyboardEvent} e
   * @returns
   */
  const ignoreKeys = e => {
    if (
      e.key.match(/[^\dBackspaceArrowLeftArrowRightDeleteTab]/) &&
      !(e.ctrlKey || (e.metaKey && e.key === "v"))
    )
      return e.preventDefault();
  };

  /**
   * reformat the phone number if country changes
   */
  useEffect(() => {
    if (field.value)
      formik.setFieldValue(
        name,
        formatIncompletePhoneNumber(field.value, country)
      );
  }, [country]);

  return (
    <>
      <ChonkyInput
        name={name}
        type="tel"
        value={field.value}
        onBlur={onBlur}
        onChange={handleInput}
        onKeyDown={ignoreKeys}
        {...rest}
      />
    </>
  );
};
